// Shared display helpers for primary (top-level) mission surfaces.

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AutoModeOutlinedIcon from "@mui/icons-material/AutoModeOutlined";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

export const AUTONOMY_LABELS = {
  FULL_INSTRUCTED: "Full instructed",
  PARTIAL_AUTONOMY: "Partial autonomy",
  FULL_AUTONOMY: "Full autonomy",
};

export const STATUS_LABELS = {
  PLANNING: "Planning",
  ACTIVE: "Active",
  PAUSED: "Paused",
  WAITING: "Waiting",
  BLOCKED: "Blocked",
  COMPLETE: "Complete",
  STOPPED: "Stopped",
  ERROR: "Error",
};

// planApprovalRequired + createPolicy mirror the backend PrimaryMissionTemplates defaults so the
// new-mission form reflects the chosen template (the user can still override before creating).
export const PRIMARY_TEMPLATES = [
  {
    id: "mission_existing_only",
    label: "Existing resources only",
    planApprovalRequired: false,
    createPolicy: "NONE",
  },
  {
    id: "mission_required_plus",
    label: "Required + fill gaps",
    planApprovalRequired: false,
    createPolicy: "CREATE_AUTO",
  },
  {
    id: "mission_standard",
    label: "Standard",
    planApprovalRequired: false,
    createPolicy: "CREATE_AUTO",
  },
  {
    id: "mission_full",
    label: "Full autonomy",
    planApprovalRequired: false,
    createPolicy: "CREATE_AUTO",
  },
  {
    id: "mission_workflow_program",
    label: "Workflow program",
    planApprovalRequired: false,
    createPolicy: "DRAFT_ONLY",
  },
];

export function templatePlanApprovalDefault(templateId) {
  const t = PRIMARY_TEMPLATES.find((x) => x.id === templateId);
  return t ? t.planApprovalRequired : false;
}

export function templateCreatePolicyDefault(templateId) {
  const t = PRIMARY_TEMPLATES.find((x) => x.id === templateId);
  return t ? t.createPolicy : "CREATE_AUTO";
}

export const AUTONOMY_OPTIONS = [
  {
    id: "FULL_INSTRUCTED",
    label: AUTONOMY_LABELS.FULL_INSTRUCTED,
    description: "Use linked and existing resources only — the orchestrator cannot create new ones.",
    icon: AssignmentOutlinedIcon,
  },
  {
    id: "PARTIAL_AUTONOMY",
    label: AUTONOMY_LABELS.PARTIAL_AUTONOMY,
    description: "Must honor required resources; may create workflows, forms, and tables to fill gaps.",
    icon: TuneOutlinedIcon,
  },
  {
    id: "FULL_AUTONOMY",
    label: AUTONOMY_LABELS.FULL_AUTONOMY,
    description: "Broad freedom within tool-approval and resource policy — minimal human gates.",
    icon: RocketLaunchOutlinedIcon,
  },
];

// Create policy controls how the orchestrator's create/adapt tools are gated (mission-briefing
// §10, §13). Labels are user-facing; ids match the backend MissionCreatePolicy enum.
export const CREATE_POLICY_LABELS = {
  CREATE_AUTO: "Auto-run",
  CREATE_WITH_REVIEW: "Review each create",
  DRAFT_ONLY: "Draft only",
  NONE: "Block creates",
};

export const CREATE_POLICY_OPTIONS = [
  {
    id: "CREATE_AUTO",
    label: CREATE_POLICY_LABELS.CREATE_AUTO,
    description: "New resources publish without a human approval step.",
    icon: BoltOutlinedIcon,
  },
  {
    id: "CREATE_WITH_REVIEW",
    label: CREATE_POLICY_LABELS.CREATE_WITH_REVIEW,
    description: "Each create or adapt pauses until you approve it.",
    icon: FactCheckOutlinedIcon,
  },
  {
    id: "DRAFT_ONLY",
    label: CREATE_POLICY_LABELS.DRAFT_ONLY,
    description: "The orchestrator may draft resources but never publish them.",
    icon: EditNoteOutlinedIcon,
  },
  {
    id: "NONE",
    label: CREATE_POLICY_LABELS.NONE,
    description: "Use only resources that already exist in the project.",
    icon: BlockOutlinedIcon,
  },
];

export function createPolicyLabel(policy) {
  return CREATE_POLICY_LABELS[policy] || policy || "—";
}

export const COMPLETION_OPTIONS = [
  {
    id: "AUTO",
    label: "Auto",
    description: "Close the mission when the orchestrator believes the objective is met.",
    icon: AutoModeOutlinedIcon,
  },
  {
    id: "CONFIRM",
    label: "Confirm",
    description: "Ask for your confirmation before marking the mission complete.",
    icon: HelpOutlineOutlinedIcon,
  },
  {
    id: "MANUAL",
    label: "Manual",
    description: "Only a human closes the mission from Mission Home.",
    icon: BackHandOutlinedIcon,
  },
];

/** Maps mission option rows to NodeCardSelector option shape. */
export function toCardSelectorOptions(options) {
  return (options || []).map((option) => ({
    value: option.id,
    label: option.label,
    description: option.description,
    icon: option.icon,
    disabled: option.disabled,
  }));
}

export const ORCHESTRATOR_KIND_OPTIONS = [
  { id: "AGENT", label: "Agent — a single orchestrator agent" },
  { id: "TEAM", label: "Team — a team orchestrates" },
];

// Resource kinds the orchestrator can be required to use for a named role (briefing §10, §11.2).
export const RESOURCE_KIND_OPTIONS = [
  { id: "AGENT", label: "Agent" },
  { id: "TEAM", label: "Team" },
  { id: "WORKFLOW", label: "Workflow" },
  { id: "FORM", label: "Form" },
  { id: "BOARD", label: "Board" },
  { id: "TABLE", label: "Table" },
  { id: "TICKET", label: "Ticket" },
  { id: "FILE", label: "File" },
  { id: "ARTIFACT", label: "Artifact" },
];

export const REQUIRED_RESOURCES_MODES = {
  ALL: "ALL",
  SELECTED: "SELECTED",
};

export const REQUIRED_RESOURCES_MODE_OPTIONS = [
  {
    id: REQUIRED_RESOURCES_MODES.ALL,
    label: "All resources",
    description:
      "The orchestrator picks agents, workflows, forms, and tables within its autonomy level.",
  },
  {
    id: REQUIRED_RESOURCES_MODES.SELECTED,
    label: "Selected resources",
    description:
      "Require specific resource kinds or IDs for named roles before the mission can complete.",
  },
];

export function resolveRequiredResourcesMode(resources, explicitMode) {
  if (
    explicitMode === REQUIRED_RESOURCES_MODES.ALL ||
    explicitMode === REQUIRED_RESOURCES_MODES.SELECTED
  ) {
    return explicitMode;
  }
  return (resources?.length ?? 0) > 0
    ? REQUIRED_RESOURCES_MODES.SELECTED
    : REQUIRED_RESOURCES_MODES.ALL;
}

export function autonomyLabel(level) {
  return AUTONOMY_LABELS[level] || level || "—";
}

export const ARTIFACT_INSTANCE_PREFIX = "art_";

/** Artifact instance ids ({@code art_*}) are not project files — coerce mislabeled FILE rows. */
export function resolveMissionResourceKind(kind, resourceId) {
  const id = (resourceId || "").trim();
  const upper = (kind || "").toUpperCase();
  if (upper === "FILE" && id.startsWith(ARTIFACT_INSTANCE_PREFIX)) {
    return "ARTIFACT";
  }
  return upper;
}

export function missionResourceTitleKey(kind, resourceId) {
  const id = (resourceId || "").trim();
  if (!id) return "";
  return `${resolveMissionResourceKind(kind, id)}:${id}`;
}

/** Admin route for a linked mission resource (resource-map-briefing.md §3). Null when unknown. */
export function missionResourcePath(kind, resourceId) {
  const id = (resourceId || "").trim();
  if (!id) return null;

  switch (resolveMissionResourceKind(kind, id)) {
    case "AGENT":
      return `/agents/${id}`;
    case "TEAM":
      return `/teams/${id}`;
    case "WORKFLOW":
      return `/project/canvas/${id}`;
    case "FORM":
      return `/forms/${id}`;
    case "BOARD":
      return `/boards/${id}`;
    case "TABLE":
      return `/databases/database_management/${id}`;
    case "TICKET": {
      const sep = id.includes(":") ? ":" : id.includes("/") ? "/" : null;
      if (!sep) return null;
      const [boardId, ticketId] = id.split(sep);
      if (!boardId || !ticketId) return null;
      return `/boards/${boardId}?ticket=${encodeURIComponent(ticketId)}`;
    }
    case "FILE":
      return "/files";
    case "ARTIFACT":
      return `/artifacts/instances/${encodeURIComponent(id)}`;
    default:
      return null;
  }
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || status || "—";
}

/** Maps mission lifecycle status to StatusPill variant tokens used across the app. */
export function missionStatusVariant(status) {
  switch (status) {
    case "ACTIVE":
      return "blue";
    case "PLANNING":
      return "green";
    case "COMPLETE":
      return "green";
    case "PAUSED":
    case "WAITING":
    case "BLOCKED":
      return "amber";
    case "STOPPED":
    case "ERROR":
      return "red";
    default:
      return "neutral";
  }
}

// Derives a prominent blocker banner from mission state (Phase 4): WAITING/PAUSED/ERROR/BLOCKED
// surface why the mission is not advancing, so a human can act (resume, wake, or confirm).
export function missionBlocker(mission) {
  if (!mission) return null;
  switch (mission.status) {
    case "ERROR":
      return {
        tone: "error",
        title: "Mission error",
        detail: mission.error || "The mission stopped due to an error.",
      };
    case "WAITING":
      return mission.completionProposed
        ? {
            tone: "wait",
            title: "Awaiting completion confirmation",
            detail:
              "The orchestrator proposed completing this mission and is waiting for a human to confirm.",
          }
        : {
            tone: "wait",
            title: "Waiting on input",
            detail:
              'Blocked until awaited input arrives. Use "Wake now" once it is ready to resume.',
          };
    case "PAUSED":
      return {
        tone: "wait",
        title: "Paused",
        detail: mission.error || "Paused until a blocker is cleared. Resume to continue.",
      };
    case "BLOCKED":
      return {
        tone: "wait",
        title: "Blocked",
        detail: mission.error || "The orchestrator is blocked on something outside its control.",
      };
    default:
      return null;
  }
}

export function formatMissionDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Top accent bar on mission list cards — maps lifecycle status to status accent tokens. */
export function missionCardAccentModifier(status) {
  switch (status) {
    case "COMPLETE":
      return "complete";
    case "PAUSED":
    case "WAITING":
    case "BLOCKED":
      return "paused";
    case "STOPPED":
    case "ERROR":
      return "stopped";
    default:
      return "active";
  }
}

/** Compact countdown for nextWakeAt when within ~48h; null → caller uses formatMissionDate. */
export function formatMissionWakeCountdown(value) {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - Date.now();
  if (ms < 0) return "overdue";
  if (ms < 60_000) return "under 1m";
  if (ms < 3_600_000) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 86_400_000 * 2) {
    const h = Math.floor(ms / 3_600_000);
    const m = Math.round((ms % 3_600_000) / 60_000);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return null;
}

export function missionListFooterPrimary(mission) {
  if (!mission) return "—";
  if (mission.status === "COMPLETE") {
    return mission.completedAt
      ? `Completed ${formatMissionDate(mission.completedAt)}`
      : "Complete";
  }
  if (mission.status === "PAUSED") return "Paused";
  if (mission.status === "STOPPED") return "Stopped";
  if (mission.status === "ERROR") return "Error";
  if (mission.status === "WAITING") return "Waiting";
  if (mission.status === "BLOCKED") return "Blocked";
  if (mission.nextWakeAt) {
    const countdown = formatMissionWakeCountdown(mission.nextWakeAt);
    if (countdown) return countdown;
    return formatMissionDate(mission.nextWakeAt);
  }
  if (mission.status === "PLANNING") return "Planning";
  return "—";
}

export function missionListFooterSecondary(mission) {
  if (!mission) return "—";
  const workers = mission.harnessMissionIds?.length ?? 0;
  const required = mission.resourceAutonomy?.requiredResources?.length ?? 0;
  if (workers > 0 && required > 0) {
    return `${required} required · ${workers} worker${workers === 1 ? "" : "s"}`;
  }
  if (workers > 0) {
    return `${workers} worker${workers === 1 ? "" : "s"}`;
  }
  if (required > 0) {
    return `${required} resource${required === 1 ? "" : "s"}`;
  }
  return formatMissionDate(mission.createdAt);
}

export function missionListShowsWakeCountdown(mission) {
  if (!mission?.nextWakeAt) return false;
  if (["COMPLETE", "STOPPED", "ERROR", "PAUSED"].includes(mission.status)) {
    return false;
  }
  return Boolean(formatMissionWakeCountdown(mission.nextWakeAt));
}

/**
 * stage-history dot indices (TicketDetail.css): 0=blue, 1=purple, 2=amber, 3=green, 4=red.
 * Same event type → same dot, regardless of position in the feed (not index % 5).
 */
export const MISSION_ACTIVITY_DOT = {
  LIFECYCLE: 0,
  PLAN: 1,
  EVENT: 2,
  RESOURCE: 3,
  PAUSE: 4,
};

const MISSION_ACTIVITY_KIND_DOT = {
  lifecycle: MISSION_ACTIVITY_DOT.LIFECYCLE,
  plan: MISSION_ACTIVITY_DOT.PLAN,
  event: MISSION_ACTIVITY_DOT.EVENT,
  resource: MISSION_ACTIVITY_DOT.RESOURCE,
  pause: MISSION_ACTIVITY_DOT.PAUSE,
};

/** Classify a mission activityLog entry to a stable stage-history dot index. */
export function missionActivityDotIndex(entry) {
  const message =
    typeof entry === "string"
      ? entry
      : entry?.message || (typeof entry === "object" ? "" : String(entry ?? ""));
  const kind = entry?.kind;
  if (kind && MISSION_ACTIVITY_KIND_DOT[kind] !== undefined) {
    return MISSION_ACTIVITY_KIND_DOT[kind];
  }
  const text = message.trim();
  if (!text) return MISSION_ACTIVITY_DOT.LIFECYCLE;

  if (/^plan updated/i.test(text)) return MISSION_ACTIVITY_DOT.PLAN;
  if (/^mission orchestration started|^mission resumed/i.test(text)) {
    return MISSION_ACTIVITY_DOT.LIFECYCLE;
  }
  if (
    /^manual review requested|^scheduling wake|^wake queued|^orchestrator running/i.test(
      text
    )
  ) {
    return MISSION_ACTIVITY_DOT.EVENT;
  }
  if (/^mission paused|^mission stopped/i.test(text)) {
    return MISSION_ACTIVITY_DOT.PAUSE;
  }
  if (/^(linked |added owned |spawned worker)/i.test(text)) {
    return MISSION_ACTIVITY_DOT.RESOURCE;
  }
  if (/^unlinked /i.test(text)) return MISSION_ACTIVITY_DOT.EVENT;

  return MISSION_ACTIVITY_DOT.EVENT;
}
